"use client";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { COLORS } from "@/lib/helpers/colors";
import { initPosthog } from "@/lib/services/posthog";
import { CssBaseline } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { mainFont, Toaster } from "@zyno/ui";
import { SessionProvider } from "next-auth/react";
import { Router } from "next/router";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useMemo } from "react";
import { ToastProvider } from "./hooks/use-toast";

// Keep your existing type augmentation
declare module "@mui/material/styles" {
  interface Palette {
    hover: {
      royal: string;
      light: string;
    };
  }
  interface PaletteOptions {
    hover: {
      royal: string;
      light: string;
    };
    border: {
      color: string;
    };
  }

  interface PaletteColor {
    100: string;
    200: string;
    300: string;
    400: string;
  }
}

const baseTheme = {
  typography: {
    fontFamily: mainFont.style.fontFamily,
    button: {
      fontFamily: mainFont.style.fontFamily,
    },
  },
};

export const lightTheme = {
  ...baseTheme,
  palette: {
    mode: "light" as const,
    primary: {
      main: COLORS.primary.main,
      light: COLORS.primary.light,
      dark: COLORS.primary.dark,
      contrastText: "#000000",
    },
    secondary: {
      main: COLORS.secondary.main,
      light: COLORS.secondary.light,
      dark: COLORS.secondary.dark,
      contrastText: "#000000",
    },
    background: {
      default: COLORS.background.light,
      paper: COLORS.paper.light,
    },
    border: {
      color: COLORS.border.light,
    },
    grey: {
      100: COLORS.grey.light[100],
      200: COLORS.grey.light[200],
      300: COLORS.grey.light[300],
      400: COLORS.grey.light[400],
    },
    hover: {
      royal: COLORS.hover.royal,
      light: COLORS.hover.light,
    },
  },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

export function Providers({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    initPosthog();
    const handleRouteChange = () => posthog?.capture("$pageview");

    Router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  const theme = useMemo(() => createTheme(lightTheme), []);

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <PostHogProvider client={posthog}>
        <ToastProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ErrorBoundary>
              <SessionProvider>{children}</SessionProvider>
            </ErrorBoundary>
          </ThemeProvider>
        </ToastProvider>
      </PostHogProvider>
    </QueryClientProvider>
  );
}
